import { Component, ElementRef, Input, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { MentionsUtils } from 'projects/shared/mentions.utils';
import { Mentionable } from 'projects/shared/message-editor-with-mentions/mentionable.model';
import { EnvironmentService } from 'projects/shared/services/environment.service';
import { Subscription } from 'rxjs';
import { ApplicationContext } from '../../models/application-context.model';
import { BorrowerAndAgentMessageModel } from '../../models/borrower-portal/borrower-and-agent-message-model';
import { BorrowerLoanMessageModel } from '../../models/borrower-portal/borrower-loan-message-model';
import { User } from '../../models/user.model';
import { ApplicationContextService } from '../../services/application-context.service';
import { BasePortalService } from '../../services/base-portal.service';
import { NotificationService } from '../../services/notification.service';
import { SignalRService } from '../../services/signalr.service';

@Component({
    selector: 'loan-messages',
    templateUrl: './loan-messages.component.html',
    styleUrls: ['./loan-messages.component.scss'],
    standalone: false
})
export class LoanMessagesComponent implements OnInit, OnDestroy {

  @ViewChild('messageEditor') messageEditorComponent: any;
  @ViewChild('scrollMe') scrollContainer: ElementRef;

  @Input() loanId: number;
  @Input() sendWhenHitEnter: boolean = false;
  @Input() borrowerName: string = '';

  userId: string;

  messages: BorrowerLoanMessageModel[] = [];

  messageCount: number = 0;
  messageText: string;
  newMessage: BorrowerAndAgentMessageModel = new BorrowerAndAgentMessageModel();

  isSaving: boolean = false;
  isOtherPartyTyping: boolean = false;

  private _lastMessageId: number = 0; // Track last message ID to detect new messages

  usersThatCanBeMentioned: Mentionable[] = [];
  private _signalRNewMessageSub?: Subscription;
  private _signalRTypingSub?: Subscription;
  private _signalRConnectedSub?: Subscription;
  private _typingHideTimer?: ReturnType<typeof setTimeout>;
  private _typingThrottleTimer?: ReturnType<typeof setTimeout>;
  private _fallbackPollTimer?: ReturnType<typeof setInterval>;

  /** How long (ms) to show the typing indicator after the last typing event */
  private static readonly TYPING_DISPLAY_TIMEOUT_MS = 3000;

  /** Minimum interval (ms) between outgoing typing indicator signals */
  private static readonly TYPING_THROTTLE_MS = 2000;

  /** How often (ms) to poll for messages when SignalR is disconnected */
  private static readonly FALLBACK_POLL_INTERVAL_MS = 10000;

  // Track current language so we can key the message editor's @if on it,
  // forcing a re-mount (which re-inits CKEditor with the new placeholder)
  // whenever the user switches language. CKEditor bakes its placeholder into
  // config at init time and doesn't react to Input changes.
  currentLang: string = 'en';
  private _langChangeSub?: Subscription;

  constructor(
    private readonly _portalService: BasePortalService,
    private readonly _signalRService: SignalRService,
    private readonly _notifyService: NotificationService,
    private readonly _environmentService: EnvironmentService,
    private readonly _applicationContextService: ApplicationContextService,
    private readonly _ngZone: NgZone,
    private readonly _transloco: TranslocoService,
  ) {
  }

  ngOnInit(): void {
    this.currentLang = this._transloco.getActiveLang();
    this._langChangeSub = this._transloco.langChanges$.subscribe(lang => {
      this.currentLang = lang;
    });
    this._applicationContextService.context.subscribe(context => {
      this.initialize(context);
    })
  }

  ngOnDestroy() {
    this._langChangeSub?.unsubscribe();
    this._signalRNewMessageSub?.unsubscribe();
    this._signalRTypingSub?.unsubscribe();
    this._signalRConnectedSub?.unsubscribe();
    this._stopFallbackPolling();
    if (this._typingHideTimer) {
      clearTimeout(this._typingHideTimer);
    }
    if (this._typingThrottleTimer) {
      clearTimeout(this._typingThrottleTimer);
    }
  }

  postMessage = () => {
    const messageText = (this.messageText || "").trim();
    if (messageText === "") {
      return;
    }

    this.newMessage.messageText = messageText;

    this.isSaving = true;
    this._portalService.postMessage(this.newMessage)
      .subscribe({
        next: () => {
          this.messageText = "";
          this.messageEditorComponent.reset();
          this.loadMessages(true); // Scroll to bottom after sending message
        },
        error: (err) => {
          this._notifyService.showError(err?.message || "Couldn't send message.", "Error!");
        }
      }).add(() => {
        this.isSaving = false;
      })
  }

  onMessageChanged = (message: string) => {
    this.messageText = message;
    this._emitTypingIndicator();
  }

  onEnterHit = () => {
    if (this.sendWhenHitEnter) {
      this.postMessage();
    }
  }

  private initialize = (context: ApplicationContext) => {
    this.userId = context.userPermissions.userId;

    this.newMessage.applicationId = this.loanId;

    this.loadMessages(true); // Scroll to bottom on initial load to show latest messages
    this.initializeMentionables();
    this._subscribeToSignalRMessages();
    this._subscribeToTypingIndicator();
    this._subscribeToConnectionState();
    this.messageText = "";
  }

  private _subscribeToSignalRMessages(): void {
    this._signalRNewMessageSub?.unsubscribe();
    this._signalRNewMessageSub = this._signalRService.newMessagePosted$.subscribe(payload => {
      if (payload.applicationId !== this.loanId) {
        return;
      }
      // Avoid duplicates
      if (this.messages.some(m => m.messageId === payload.messageId)) {
        return;
      }

      const msg = new BorrowerLoanMessageModel();
      msg.messageId = payload.messageId;
      msg.applicationId = payload.applicationId;
      msg.borrowerId = payload.borrowerId;
      msg.agentId = payload.agentId;
      msg.senderId = payload.senderId;
      msg.senderFirstName = payload.senderFirstName;
      msg.senderLastName = payload.senderLastName;
      msg.content = payload.content;
      msg.postTime = new Date(payload.postTime);
      (msg as any)['mentionableContent'] = MentionsUtils.generateDisplayHtmlWithMentions(payload.content);

      this.messages = [...this.messages, msg];
      this.messageCount = this.messages.length;
      this._lastMessageId = msg.messageId;

      // Hide typing indicator when a message arrives from the other party
      if (payload.senderId !== this.userId) {
        this.isOtherPartyTyping = false;
      }

      setTimeout(() => this.scrollToBottom(), 0);
    });
  }

  private _subscribeToTypingIndicator(): void {
    this._signalRTypingSub?.unsubscribe();
    this._signalRTypingSub = this._signalRService.typingIndicator$.subscribe(payload => {
      if (payload.applicationId !== this.loanId) {
        return;
      }
      // Ignore our own typing events
      if (payload.userId === this.userId) {
        return;
      }

      this.isOtherPartyTyping = true;
      setTimeout(() => this.scrollToBottom(), 0);

      // Reset the auto-hide timer
      if (this._typingHideTimer) {
        clearTimeout(this._typingHideTimer);
      }
      this._typingHideTimer = setTimeout(() => {
        this.isOtherPartyTyping = false;
      }, LoanMessagesComponent.TYPING_DISPLAY_TIMEOUT_MS);
    });
  }

  private _subscribeToConnectionState(): void {
    this._signalRConnectedSub?.unsubscribe();
    this._signalRConnectedSub = this._signalRService.connected$.subscribe(connected => {
      if (connected) {
        this._stopFallbackPolling();
      } else {
        this._startFallbackPolling();
      }
    });
  }

  private _startFallbackPolling(): void {
    if (this._fallbackPollTimer) {
      return; // Already polling
    }
    console.debug('[LoanMessages] SignalR disconnected — starting fallback polling');
    this._ngZone.runOutsideAngular(() => {
      this._fallbackPollTimer = setInterval(() => {
        this._ngZone.run(() => this.loadMessages());
      }, LoanMessagesComponent.FALLBACK_POLL_INTERVAL_MS);
    });
  }

  private _stopFallbackPolling(): void {
    if (this._fallbackPollTimer) {
      console.debug('[LoanMessages] SignalR connected — stopping fallback polling');
      clearInterval(this._fallbackPollTimer);
      this._fallbackPollTimer = undefined;
    }
  }

  private _emitTypingIndicator(): void {
    // Throttle: only send one signal per TYPING_THROTTLE_MS
    if (this._typingThrottleTimer) {
      return;
    }
    this._signalRService.sendTypingIndicator(this.loanId);
    this._ngZone.runOutsideAngular(() => {
      this._typingThrottleTimer = setTimeout(() => {
        this._typingThrottleTimer = undefined;
      }, LoanMessagesComponent.TYPING_THROTTLE_MS);
    });
  }

  private initializeMentionables = () => {
    this._portalService.getMentionableContacts(this.loanId).subscribe({
      next: contacts => {
        const users = (contacts ?? []).map(contact => ({
          firstName: contact.firstName,
          lastName: contact.lastName,
          avatarId: contact.avatarId ?? undefined,
          userCompanyGuid: contact.userId
        } as User));
        this.usersThatCanBeMentioned = MentionsUtils.prepareUserMentionables(
          this._environmentService.apiInfo.apiBaseUrl,
          users
        );
      },
      error: err => this._notifyService.showError(err?.message || "Couldn't load mentionable contacts.", "Error!")
    });
  }

  private loadMessages = (scrollToBottom: boolean = false) => {
    this._portalService.getMessagesForApplication(this.loanId).subscribe({
      next: (messages) => {
        this.messages = messages.sort((m1, m2) => m1.messageId - m2.messageId).map(message => {
          message["mentionableContent"] = MentionsUtils.generateDisplayHtmlWithMentions(message.content);
          return message
        });
        this.messageCount = messages.length;

        // Detect if there's a new message from someone else during periodic refresh
        let shouldScroll = scrollToBottom; // Explicit scroll (initial load or after sending)

        if (!scrollToBottom && this.messages.length > 0) {
          const lastMessage = this.messages[this.messages.length - 1];

          // If there's a new message AND it's from someone else, scroll to show it
          if (lastMessage.messageId > this._lastMessageId && lastMessage.senderId !== this.userId) {
            shouldScroll = true;
          }
        }

        // Update last message ID
        if (this.messages.length > 0) {
          this._lastMessageId = this.messages[this.messages.length - 1].messageId;
        }

        // Scroll to bottom if needed
        if (shouldScroll) {
          // Use setTimeout to ensure DOM has updated with new messages
          setTimeout(() => this.scrollToBottom(), 0);
        }
      },
      error: (err) => {
        this._notifyService.showError(err?.message || "Couldn't load application messages.", "Error!");
      }
    });
  }

  private scrollToBottom = () => {
    if (this.scrollContainer && this.scrollContainer.nativeElement) {
      this.scrollContainer.nativeElement.scrollTop = this.scrollContainer.nativeElement.scrollHeight;
    }
  }

}
